import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileSearch, Sparkles } from "lucide-react";

/**
 * Cinematic hero:
 *  - Parallax-tracked nebula gradient mesh
 *  - Central animated SVG peptide molecule with orbital rings
 *  - Floating particle field
 *  - 3D depth on hover via CSS perspective
 */
export default function NebulaHero() {
  const heroRef = useRef(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty("--mx", x.toFixed(3));
      el.style.setProperty("--my", y.toFixed(3));
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section ref={heroRef} className="nebula-hero relative overflow-hidden" data-testid="nebula-hero">
      {/* Gradient mesh layer */}
      <div className="mesh-gradient" aria-hidden />
      {/* Parallax nebula blobs */}
      <div className="hero-parallax" aria-hidden>
        <div className="nebula-blob cyan absolute" style={{ width: 580, height: 580, top: "-12%", left: "-8%" }} />
        <div className="nebula-blob magenta absolute" style={{ width: 520, height: 520, top: "20%", right: "-14%" }} />
        <div className="nebula-blob violet absolute" style={{ width: 420, height: 420, bottom: "-18%", left: "32%" }} />
      </div>
      {/* Starfield + grid */}
      <div className="absolute inset-0 starfield" aria-hidden />
      <div className="absolute inset-0 hero-grid" aria-hidden />
      <div className="absolute inset-0 scanlines pointer-events-none" aria-hidden />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 py-24 lg:py-32 z-10">
        {/* COPY */}
        <div className="lg:col-span-7 flex flex-col justify-center">
          <div className="eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
            <Sparkles className="h-3 w-3 text-[var(--terra)]" strokeWidth={2} />
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--terra)] shadow-[0_0_14px_rgba(63,255,224,0.9)]" />
            Koveralabs-Verified · Per-Batch COA · 21+ Buyers Only
          </div>
          <h1
            className="h-display text-[44px] sm:text-[64px] lg:text-[88px] mt-5 leading-[0.94] tracking-[-0.03em] text-[var(--ink)]"
            data-testid="hero-headline"
          >
            Synthesize
            <br />
            <span className="gradient-text relative">
              the unknown.
              <svg
                aria-hidden
                viewBox="0 0 400 12"
                className="absolute -bottom-2 left-0 w-full h-3 overflow-visible"
                fill="none"
              >
                <path
                  d="M2 8 Q 100 -2, 200 6 T 398 4"
                  stroke="url(#under-grad)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  pathLength="1"
                  style={{ strokeDasharray: 1, strokeDashoffset: 0 }}
                />
                <defs>
                  <linearGradient id="under-grad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3FFFE0" />
                    <stop offset="55%" stopColor="#FF4FD8" />
                    <stop offset="100%" stopColor="#8A6BFF" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
            <br />
            <span className="text-[var(--ink)]">Every batch is <span className="gradient-text">verified.</span></span>
          </h1>
          <p className="mt-7 text-[15.5px] text-[var(--ink-soft)] leading-relaxed max-w-xl">
            Reference-grade research compounds supplied to accredited laboratories and qualified researchers.
            Metabolic research peptides, GHK-Cu, NAD+, MOTS-c, bacteriostatic water and more — every batch shipped with a Koveralabs-verified COA and independent third-party identity testing.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/shop" className="btn-primary" data-testid="hero-cta-shop">
              Enter the Catalog <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/coa" className="btn-outline" data-testid="hero-cta-coa">
              <FileSearch className="h-4 w-4" /> Verify a Batch
            </Link>
          </div>

          {/* Live status chip */}
          <div className="mt-12 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[var(--paper-2)]/80 border border-[var(--line)] backdrop-blur-md w-fit">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--olive)] opacity-70 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--olive)]" />
            </span>
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--ink-soft)]">
              Live · 16 compounds · 12 active batches
            </span>
          </div>
        </div>

        {/* 3D MOLECULE STAGE */}
        <div className="lg:col-span-5 relative min-h-[460px] lg:min-h-[600px] flex items-center justify-center">
          <div className="molecule-stage relative" aria-hidden>
            {/* Orbital rings */}
            <div className="orbit orbit-1" />
            <div className="orbit orbit-2" />
            <div className="orbit orbit-3" />

            {/* Peptide SVG molecule */}
            <svg viewBox="-100 -100 200 200" className="molecule-svg" aria-hidden>
              <defs>
                <radialGradient id="atomGlowCyan" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#3FFFE0" stopOpacity="1" />
                  <stop offset="60%" stopColor="#3FFFE0" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#3FFFE0" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="atomGlowMagenta" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#FF4FD8" stopOpacity="1" />
                  <stop offset="60%" stopColor="#FF4FD8" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#FF4FD8" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="atomGlowViolet" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#8A6BFF" stopOpacity="1" />
                  <stop offset="60%" stopColor="#8A6BFF" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="#8A6BFF" stopOpacity="0" />
                </radialGradient>
                <filter id="atomBlur" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.2" />
                </filter>
              </defs>

              <g className="molecule-spin">
                {/* Bonds */}
                {BONDS.map((b, i) => (
                  <line
                    key={i}
                    x1={b[0][0]} y1={b[0][1]} x2={b[1][0]} y2={b[1][1]}
                    stroke="rgba(63,255,224,0.45)"
                    strokeWidth="0.7"
                    strokeLinecap="round"
                  />
                ))}
                {/* Atoms */}
                {ATOMS.map((a, i) => (
                  <g key={i}>
                    {/* glow halo */}
                    <circle cx={a.x} cy={a.y} r={a.r * 2.4} fill={`url(#atomGlow${a.color})`} opacity="0.7" filter="url(#atomBlur)" />
                    {/* core */}
                    <circle cx={a.x} cy={a.y} r={a.r} fill={a.fill} stroke={a.stroke} strokeWidth="0.6" />
                    {/* hilight */}
                    <circle cx={a.x - a.r * 0.32} cy={a.y - a.r * 0.32} r={a.r * 0.32} fill="rgba(255,255,255,0.55)" />
                  </g>
                ))}
              </g>

              {/* Center nucleus */}
              <circle cx="0" cy="0" r="3" fill="#3FFFE0" />
              <circle cx="0" cy="0" r="8" fill="url(#atomGlowCyan)" opacity="0.55" />
            </svg>

            {/* Floating particles */}
            <div className="particles" aria-hidden>
              {Array.from({ length: 18 }).map((_, i) => (
                <span key={i} style={{
                  left: `${(i * 37) % 100}%`,
                  top: `${(i * 53) % 100}%`,
                  animationDelay: `${(i * 0.4) % 8}s`,
                  animationDuration: `${6 + (i % 5)}s`,
                  background: i % 3 === 0 ? "#3FFFE0" : i % 3 === 1 ? "#FF4FD8" : "#8A6BFF",
                }} />
              ))}
            </div>

            {/* Tag chip — purity */}
            <div className="hero-chip chip-purity">
              <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-[var(--ink-muted)]">Latest Run · Koveralabs</div>
              <div className="font-display text-2xl font-semibold gradient-text mt-1">99.81%</div>
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--ink-soft)]">HPLC Purity · TRZ · NA26020B</div>
            </div>

            {/* Tag chip — RUO */}
            <div className="hero-chip chip-ruo">
              <div className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--magenta)] shadow-[0_0_10px_rgba(255,79,216,0.9)]" />
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--magenta)]">RUO · 21+</div>
              </div>
              <div className="mt-1 font-mono text-[10.5px] text-[var(--ink-soft)] leading-tight">For laboratory<br/>research use only</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// === Molecule data — stylized "peptide" structure ===
const r = (n) => Math.round(n * 100) / 100;
const ATOMS = [
  { x: -68, y: -22, r: 7,  fill: "#0F0F26", stroke: "#3FFFE0", color: "Cyan" },
  { x: -38, y: 18,  r: 6,  fill: "#0F0F26", stroke: "#3FFFE0", color: "Cyan" },
  { x: -8,  y: -16, r: 8,  fill: "#0F0F26", stroke: "#FF4FD8", color: "Magenta" },
  { x: 22,  y: 22,  r: 6,  fill: "#0F0F26", stroke: "#8A6BFF", color: "Violet" },
  { x: 52,  y: -12, r: 7,  fill: "#0F0F26", stroke: "#3FFFE0", color: "Cyan" },
  { x: 78,  y: 28,  r: 6,  fill: "#0F0F26", stroke: "#FF4FD8", color: "Magenta" },
  { x: -52, y: 56,  r: 5,  fill: "#0F0F26", stroke: "#8A6BFF", color: "Violet" },
  { x: 38,  y: -52, r: 5,  fill: "#0F0F26", stroke: "#3FFFE0", color: "Cyan" },
  { x: -82, y: 38,  r: 4,  fill: "#0F0F26", stroke: "#FF4FD8", color: "Magenta" },
].map((a) => ({ ...a, x: r(a.x), y: r(a.y) }));
const BONDS = [
  [[ATOMS[0].x, ATOMS[0].y], [ATOMS[1].x, ATOMS[1].y]],
  [[ATOMS[1].x, ATOMS[1].y], [ATOMS[2].x, ATOMS[2].y]],
  [[ATOMS[2].x, ATOMS[2].y], [ATOMS[3].x, ATOMS[3].y]],
  [[ATOMS[3].x, ATOMS[3].y], [ATOMS[4].x, ATOMS[4].y]],
  [[ATOMS[4].x, ATOMS[4].y], [ATOMS[5].x, ATOMS[5].y]],
  [[ATOMS[1].x, ATOMS[1].y], [ATOMS[6].x, ATOMS[6].y]],
  [[ATOMS[4].x, ATOMS[4].y], [ATOMS[7].x, ATOMS[7].y]],
  [[ATOMS[6].x, ATOMS[6].y], [ATOMS[8].x, ATOMS[8].y]],
];
